// Independent screens/components sometimes load the exact same read-only
// config list in the same tick - e.g. the "Подачи" screen's own store and
// the shell's summary strip both want channels/routes/lines the moment they
// mount together. This collapses concurrent, IDENTICAL in-flight calls into
// one network request per key.
//
// Deliberately NOT a general cache: an entry lives only while its request is
// PENDING and is removed the instant it settles (success or failure), so a
// caller that asks a second later always gets a fresh network round trip -
// this only ever shares work that is happening RIGHT NOW, never yesterday's
// answer (docs/contract-requests/C3-summary.md has the case for a real,
// longer-lived shared cache, which needs a store this package does not own).
//
// It intentionally does not thread a per-caller AbortSignal into the shared
// request: one caller unmounting must not cancel a request another, still
// mounted, caller is waiting on. The same rule binds the `run` a caller
// passes in: it must NOT close over that caller's signal either (REVIEW-1
// A9-1: the summary strip did, and every reload of the strip aborted the
// request the "Подачи" store was waiting on - which took the foreign
// AbortError for its own). A caller that wants to ignore a stale result
// after the fact still can, via its own `signal.aborted` check after the
// awaited call resolves.

/** Per "owner" (in practice: one `TypedApi`/`rest` instance - one per Services,
 *  so two independent tests with their own fake services never share a cache). */
const inflightByOwner = new WeakMap<object, Map<string, Promise<unknown>>>()

export function dedupedCall<T>(owner: object, key: string, run: () => Promise<T>): Promise<T> {
  let inflight = inflightByOwner.get(owner)
  if (!inflight) {
    inflight = new Map()
    inflightByOwner.set(owner, inflight)
  }
  const hit = inflight.get(key)
  if (hit) return hit as Promise<T>
  const promise = run().finally(() => {
    inflight!.delete(key)
  })
  inflight.set(key, promise)
  return promise
}
